import { isMonthBeforeDay } from '@/helpers/date-helpers'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'
import { TimesheetMethod } from '@/shared/constants/timesheet/TimesheetMethod'
import { RateUnitType } from '@/shared/constants/booking/RateUnitType'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import { TimesheetSubmitResult } from '@/shared/constants/timesheet/TimesheetSubmitResult'
import {
  BookingStatus,
  hasCandidateModifiedBookingOrStatedDidNotHappen,
} from '@/shared/constants/booking/BookingStatus'
import dayjs from '@/services/date/index'
import TimesheetTimeStringDetailsModel from '@/models/timesheets/timesheetTimeStringDetailsModel'
import TimesheetPayload from '@/models/timesheets/timesheetPayload'
// eslint-disable-next-line no-unused-vars
import ModifyTimesheetPayload from '@/models/timesheets/modifyTimesheetPayload'

export default {
  methods: {
    renderDate(dateUtc, timezone, format) {
      let formatString = format

      if (!formatString) {
        formatString = isMonthBeforeDay(this.$i18n.locale.toLowerCase())
          ? DateFormatToken.dddMMDDYYYYWithSlash
          : DateFormatToken.dddDDMMYYYYWithSlash
      }

      const dateTz = dateUtc.tz(timezone)
      return dateTz.format(formatString)
    },
    renderTime(dateUtc, timezone, format = DateFormatToken.HHmm) {
      return this.renderDate(dateUtc, timezone, format)
    },
    renderLocalDateToTime(date, format = DateFormatToken.HHmm) {
      return dayjs(date).format(format)
    },
    renderLocalTime(time) {
      return dayjs(time, DateFormatToken.HHmm).format(DateFormatToken.hhmma)
    },
    renderFullOrAbbreviatedTime(time, abbreviated) {
      return abbreviated ? time : this.renderLocalTime(time)
    },
    isHourlyOrHybridUnitType(timesheet) {
      const hourlyUnitTypes = [
        RateUnitType.HOURLY,
        RateUnitType.CLIENT_UNIT_CANDIDATE_HOURLY,
      ]
      return hourlyUnitTypes.includes(timesheet?.unitType)
    },
    getBreakMinutesText(minutes, abbreviated = false) {
      return minutesToHoursMinsString(minutes, abbreviated)
    },
    isCandidateTimesheeting(timesheet) {
      return timesheet?.timesheetMethod === TimesheetMethod.candidate
    },
    getOriginalBookingDetails(timesheet, abbreviated = false) {
      const timeFormatString = abbreviated
        ? DateFormatToken.HHmm
        : DateFormatToken.hhmma

      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderLocalDateToTime(
          timesheet.startTimeLocal,
          timeFormatString
        ),
        endTime: this.renderLocalDateToTime(
          timesheet.endTimeLocal,
          timeFormatString
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.breakMinutes,
        abbreviated,
      })
    },
    getModifiedBookingDetails(timesheet, abbreviated = false) {
      return new TimesheetTimeStringDetailsModel({
        startTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.startTime,
          abbreviated
        ),
        endTime: this.renderFullOrAbbreviatedTime(
          timesheet.candidateBookingModifications.endTime,
          abbreviated
        ),
        payOptionType: timesheet.payOptionType,
        breaks: timesheet.candidateBookingModifications.unpaidBreakMinutes,
        abbreviated,
      })
    },
    /**
     * Returns the original booking details or the modified details, if the
     * candidate has modified the booking
     * @param {*} timesheet
     * @param {Boolean} abbreviated
     * @returns
     */
    getModifiedOrOriginalBookingDetails(timesheet, abbreviated = false) {
      const hasCandidateModifiedTimesheet =
        timesheet.bookingStatus ===
        BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED

      return hasCandidateModifiedTimesheet
        ? this.getModifiedBookingDetails(timesheet, abbreviated)
        : this.getOriginalBookingDetails(timesheet, abbreviated)
    },
    /**
     * @param {TimesheetTimeStringDetailsModel} bookingDetails
     * @returns
     */
    generateFullBookingTimeString(bookingDetails) {
      let bookingTimeString = `${bookingDetails.startTime} - ${bookingDetails.endTime}`

      bookingTimeString += `\u00A0\u00A0${bookingDetails.payOptionType}`

      bookingTimeString += `\u00A0\u00A0${this.getBreakMinutesText(
        bookingDetails.breaks,
        bookingDetails.abbreviated
      )}`

      return bookingTimeString
    },
    hasCandidateModifiedTimesheet(timesheet) {
      return hasCandidateModifiedBookingOrStatedDidNotHappen(
        timesheet.bookingStatus
      )
    },
    isDifferencePositive(differenceInMinutes) {
      return differenceInMinutes > 0
    },
    differenceInMinutesAsPositiveInt(differenceInMinutes) {
      return Math.abs(differenceInMinutes)
    },
    mapTimesheetToPayload(timesheet) {
      let operationType = TimesheetOperationType.ACCEPT

      // If the client is accepting a booking the candidate
      // stated did not happen, convert the operation type to
      // CANCEL (client flagged booking as did not happen)
      if (
        timesheet.bookingStatus ===
        BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
      ) {
        operationType = TimesheetOperationType.CANCEL
      }

      return new TimesheetPayload({
        bookingId: timesheet.bookingId,
        locationId: timesheet.locationId,
        candidateId: timesheet.candidateId,
        operationType,
      })
    },
    /**
     * @param {ModifyTimesheetPayload} modifyTimesheetPayload
     * @returns
     */
    mapModifyTimesheetChangesToPayload(modifyTimesheetPayload) {
      return new TimesheetPayload({
        bookingId: modifyTimesheetPayload.bookingId,
        locationId: modifyTimesheetPayload.locationId,
        candidateId: modifyTimesheetPayload.candidateId,
        operationType: TimesheetOperationType.MODIFY,
        startTimeLocal: modifyTimesheetPayload.newStartTimeLocal,
        endTimeLocal: modifyTimesheetPayload.newEndTimeLocal,
        breakMinutes: modifyTimesheetPayload.newBreakMinutes,
        payOptionType: modifyTimesheetPayload.newPayOptionType,
      })
    },
    mapTimesheetListToPayload(timesheets) {
      return timesheets.map(this.mapTimesheetToPayload)
    },

    determineCountFromSubmissionResult(timesheet, operationType, issue) {
      if (!issue) {
        switch (operationType) {
          case TimesheetOperationType.ACCEPT:
            // determine status by timesheet method and status of the booking
            switch (timesheet.bookingStatus) {
              case BookingStatus.ECH_NOT_APPROVED:
              case BookingStatus.SCHOOL_NOT_APPROVED:
                if (timesheet.timesheetMethod === TimesheetMethod.client)
                  return { approvedCount: 1, pendingReviewCount: 0 }
                return { approvedCount: 0, pendingReviewCount: 1 }
              case BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_NO_CHANGES:
                return { approvedCount: 1, pendingReviewCount: 0 }
              case BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED:
                return { approvedCount: 1, pendingReviewCount: 0 }
              case BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN:
                return { approvedCount: 0, pendingReviewCount: 1 }
              default:
                return { approvedCount: 0, pendingReviewCount: 1 }
            }
          case TimesheetOperationType.CANCEL:
            return { approvedCount: 0, pendingReviewCount: 1 }
          case TimesheetOperationType.MODIFY:
            return { approvedCount: 0, pendingReviewCount: 1 }
          default:
            return { approvedCount: 0, pendingReviewCount: 0 }
        }
      }

      switch (issue.status) {
        case TimesheetSubmitResult.alreadyApprovedForInvoice:
          return { approvedCount: 1, pendingReviewCount: 0 }
        case TimesheetSubmitResult.alreadyActioned:
          return { approvedCount: 0, pendingReviewCount: 1 }
        default:
          return { approvedCount: 0, pendingReviewCount: 0 }
      }
    },
  },
}
